import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';

/** Retention value: a duration (timedelta units) or 'never' (no expiry). */
export type RetentionTtl = 'never' | { [unit: string]: number };

export interface IGroupRetention {
  subscription: string;
  group: string;
  ttl: RetentionTtl;
  is_default: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class KrulesService {
  _host: string = '';
  _apiPath: string = '/api/v2';
  _basePath: string = `${this._host}${this._apiPath}`;
  constructor(private http: HttpClient) {}

  /**
   * Fetch the list of subscription IDs visible to the caller. The backend
   * encapsulates all multitenant-mode + admin logic; the frontend treats
   * the response uniformly across modes.
   * See backend_api_v2/auth.py: create_subscription_lister.
   */
  getSubscriptions(token: string): Promise<string[]> {
    return lastValueFrom(
      this.http.get<string[]>(`${this._host}/api/v2/subscriptions/`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    );
  }

  deleteGroup(token: string, subscriptionId: string, groupId: string) {
    const url = `${this._basePath}/${subscriptionId}/${groupId}`;
    return this.http.delete(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
  /**
   * Per-group retention of the event-sourcing history.
   * See backend_api_v2/routers.py: get/set/reset_group_retention.
   */
  getRetention(token: string, subscriptionId: string, groupId: string) {
    return this.http.get<IGroupRetention>(this._retentionUrl(subscriptionId, groupId), {
      headers: { Authorization: `Bearer ${token}` },
    });
  }
  setRetention(
    token: string,
    subscriptionId: string,
    groupId: string,
    ttl: RetentionTtl
  ) {
    return this.http.put<IGroupRetention>(
      this._retentionUrl(subscriptionId, groupId),
      { ttl },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }
  resetRetention(token: string, subscriptionId: string, groupId: string) {
    return this.http.delete<IGroupRetention>(this._retentionUrl(subscriptionId, groupId), {
      headers: { Authorization: `Bearer ${token}` },
    });
  }
  private _retentionUrl(subscriptionId: string, groupId: string) {
    return `${this._basePath}/${subscriptionId}/groups/${encodeURIComponent(groupId)}/retention`;
  }

  deleteEntity(
    token: string,
    subscriptionId: string,
    groupId: string,
    entityId: string
  ) {
    const url = `${this._basePath}/${subscriptionId}/${groupId}/${entityId}`;
    return this.http.delete(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
}
